import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleOnSettled } from '@utils/formHelpers';
import { useTranslation } from 'react-i18next';
import { localStore } from '@utils/localStore';
import {
  FormattedSignInData,
  FormattedSignInResp,
  SignInReq,
  useSignIn,
} from '@services/auth/loginUser';
import { useErrors } from '@hooks/errors';
import { useQueryParams } from '@hooks/queryParams';
import { updateSlug } from '@hooks/slug/helpers';

export const useLogInForm = ({ successHandler }: { successHandler: () => void }) => {
  const handleErrors = useErrors();
  const { setParams } = useQueryParams();
  const { t } = useTranslation('app');

  const formSchema = z.object({
    email: z
      .string()
      .trim()
      .email({ message: t('forms.email.required') }),
    password: z
      .string()
      .trim()
      .min(8, {
        message: t('forms.password.required'),
      }),
  });

  const defaultValues: SignInReq = {
    email: '',
    password: '',
  };
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const updateAuthData = ({ type, data }: FormattedSignInData) => {
    const item = localStore.getItem(`app-${type}`);
    const parsed = typeof item === 'string' ? (JSON.parse(item) as Record<string, string>) : item;
    if (type === 'admin') localStore.updateItem('app-admin', { ...parsed, id: data.id });
    if (type === 'user') {
      setParams({ auth: JSON.stringify({ ...parsed, id: data.id }) }, { disableSaveFilters: true });
      if (data.role === 'foreman') {
        localStore.updateItem('app-user', { ...parsed, id: data.id });
        localStore.updateItem('app-foreman', { enabled: true });
      } else updateSlug(data.tenant.slug);
    }
  };

  const callback = (resp: FormattedSignInResp) => {
    const errors = resp.errors || [];
    handleOnSettled<FormattedSignInResp>(
      resp,
      (values) => {
        const data = values as unknown as FormattedSignInData;
        updateAuthData(data);
        successHandler();
      },
      () =>
        handleErrors({ root: errors?.[0] || '', ...errors }, (key, message) =>
          form.setError(key as keyof z.infer<typeof formSchema>, { message }),
        ),
    );
  };

  const { mutate, isLoading } = useSignIn({ callback });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    mutate(values);
  };

  return {
    form,
    onSubmit,
    locked: isLoading,
  };
};
